import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'preact/hooks';
import { usePatch } from '@core/utils/hooks/use-patch';
import { usePost } from '@core/utils/hooks/use-post';

export function useApiKeysMutations(fetchCacheKey) {
    const queryClient = useQueryClient();

    const { patch: invalidateKey } = usePatch('/keys/invalidate', {
        successMessage: 'Successfully invalidated API key',
    });
    const { patch: activateKey } = usePatch('/keys/activate', {
        successMessage: 'Successfully activated API key',
    });
    const { patch: extendKey } = usePatch('/keys/extend', {
        successMessage: 'Successfully extended API key',
    });
    const { post: generateKey } = usePost('/keys', {
        successMessage: 'Successfully generated new API key',
    });

    const upsertOne = (oldData, updated) => {
        if (!Array.isArray(oldData)) return updated;
        const idx = oldData.findIndex((k) => k?.keyId === updated?.keyId);
        if (idx === -1) return [updated, ...oldData];
        const copy = oldData.slice();
        copy[idx] = updated;
        return copy;
    };

    const prependOne = (oldData, added) => {
        if (!Array.isArray(oldData)) return [added];
        return [added, ...oldData];
    };

    const makeOnSuccessUpdater = (transform) => (response) => {
        if (!fetchCacheKey || !response) return;
        queryClient.setQueryData(fetchCacheKey, (old) => transform(old, response));
    };

    const invalidateKeyMutation = useMutation({
        mutationFn: async (payload) => (await invalidateKey(payload))?.data,
        onSuccess: makeOnSuccessUpdater(upsertOne),
        onSettled: () =>
            fetchCacheKey && queryClient.invalidateQueries({ queryKey: fetchCacheKey }),
    });

    const activateKeyMutation = useMutation({
        mutationFn: async (payload) => (await activateKey(payload))?.data,
        onSuccess: makeOnSuccessUpdater(upsertOne),
        onSettled: () =>
            fetchCacheKey && queryClient.invalidateQueries({ queryKey: fetchCacheKey }),
    });

    const extendKeyMutation = useMutation({
        mutationFn: async (payload) => (await extendKey(payload))?.data,
        onSuccess: makeOnSuccessUpdater(upsertOne),
        onSettled: () =>
            fetchCacheKey && queryClient.invalidateQueries({ queryKey: fetchCacheKey }),
    });

    const generateKeyMutation = useMutation({
        mutationFn: async (payload) => (await generateKey(payload))?.data,
        onSuccess: makeOnSuccessUpdater(prependOne),
        onSettled: () =>
            fetchCacheKey && queryClient.invalidateQueries({ queryKey: fetchCacheKey }),
    });

    const isSubmitting = useMemo(
        () =>
            invalidateKeyMutation.isPending ||
            activateKeyMutation.isPending ||
            extendKeyMutation.isPending ||
            generateKeyMutation.isPending,
        [
            invalidateKeyMutation.isPending,
            activateKeyMutation.isPending,
            extendKeyMutation.isPending,
            generateKeyMutation.isPending,
        ]
    );

    return {
        isSubmitting,
        invalidateKeyMutation,
        activateKeyMutation,
        extendKeyMutation,
        generateKeyMutation,
    };
}
